import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Search, Pencil, Trash2, MoreVertical, ScrollText, Sparkles, PencilLine } from 'lucide-react';
import { useAdminRecipesList, useDeleteRecipe } from '@/features/recipes/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Dialog } from '@/components/ui/Dialog';
import { Card } from '@/components/ui/Card';
import { RecipeLogDialog } from '@/features/ai-logs/RecipeLogDialog';
import type { Recipe } from '@honeycomb/shared';

export const Route = createFileRoute('/_authed/recipes/')({
  component: RecipesList,
});

function RecipesList() {
  const [query, setQuery] = useState('');
  const trimmed = query.trim();

  const { data, isLoading } = useAdminRecipesList({
    search: trimmed || undefined,
    limit: 100,
  });
  const recipes = data?.recipes ?? [];
  const total = data?.total ?? 0;

  const [toDelete, setToDelete] = useState<Recipe | null>(null);
  const [logRecipeId, setLogRecipeId] = useState<string | null>(null);
  const del = useDeleteRecipe();
  const navigate = useNavigate();

  const onConfirmDelete = async () => {
    if (!toDelete) return;
    await del.mutateAsync(toDelete.id);
    setToDelete(null);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <header className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl leading-tight">Recipes</h1>
          <p className="text-ink-soft text-sm mt-1">
            All recipes across all users{total ? ` · ${total}` : ''}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate({ to: '/recipes/generate' })}>
            <Sparkles size={16} /> Generate with AI
          </Button>
          <Button onClick={() => navigate({ to: '/recipes/new' })}>
            <Plus size={16} /> New recipe
          </Button>
        </div>
      </header>

      <div className="mb-4 max-w-md">
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint">
            <Search size={16} />
          </div>
          <Input
            placeholder="Search title or ingredients…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <Card shadow="sm" className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-ink-soft">Loading…</div>
        ) : recipes.length === 0 ? (
          <div className="p-6 text-ink-soft">{trimmed.length > 0 ? 'No matches.' : 'No recipes yet.'}</div>
        ) : (
          <>
            {/* Desktop table — hidden below md */}
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr
                    className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
                    style={{ borderColor: 'var(--color-hairline)' }}
                  >
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3">Author</th>
                    <th className="px-4 py-3">Owner</th>
                    <th className="px-4 py-3">Source</th>
                    <th className="px-4 py-3">Visibility</th>
                    <th className="px-4 py-3">Difficulty</th>
                    <th className="px-4 py-3">Updated</th>
                    <th className="px-4 py-3 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {recipes.map((r) => (
                    <tr
                      key={r.id}
                      className="border-b last:border-b-0 hover:bg-cream-deep/60"
                      style={{ borderColor: 'var(--color-hairline)' }}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium">{r.title}</span>
                          {r.aiGenerated && (
                            <span title="AI-generated"><Sparkles size={13} className="text-honey shrink-0" /></span>
                          )}
                          {r.aiEdited && (
                            <span title="AI-edited"><PencilLine size={13} className="text-sage shrink-0" /></span>
                          )}
                        </div>
                        <div className="font-mono text-xs text-ink-faint break-all">{r.id}</div>
                      </td>
                      <td className="px-4 py-3 text-ink-soft">{r.author || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-ink-faint break-all">{r.ownerId}</span>
                      </td>
                      <td className="px-4 py-3">
                        <SourceBadge source={r.source} />
                      </td>
                      <td className="px-4 py-3">
                        <VisibilityBadge visibility={r.visibility} />
                      </td>
                      <td className="px-4 py-3 text-ink-soft">{r.difficulty}</td>
                      <td className="px-4 py-3 text-ink-soft font-mono text-xs">
                        {formatRelative(r.updatedAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <Link
                            to="/recipes/$id/edit"
                            params={{ id: r.id }}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-ink-soft hover:bg-honey-pale hover:text-ink"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </Link>
                          <RowMenu
                            buttonSize={32}
                            iconSize={14}
                            onViewLog={() => setLogRecipeId(r.id)}
                            onDelete={() => setToDelete(r)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card stack — hidden from md up */}
            <ul
              className="md:hidden divide-y"
              style={{ borderColor: 'var(--color-hairline)' }}
            >
              {recipes.map((r) => (
                <li key={r.id} className="px-4 py-4">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-sm leading-snug">{r.title}</span>
                    {r.aiGenerated && (
                      <span title="AI-generated"><Sparkles size={13} className="text-honey shrink-0" /></span>
                    )}
                    {r.aiEdited && (
                      <span title="AI-edited"><PencilLine size={13} className="text-sage shrink-0" /></span>
                    )}
                  </div>
                  <div className="font-mono text-xs text-ink-faint break-all mt-0.5">{r.id}</div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <SourceBadge source={r.source} />
                    <VisibilityBadge visibility={r.visibility} />
                    {r.difficulty ? (
                      <span className="text-xs text-ink-soft">{r.difficulty}</span>
                    ) : null}
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-ink-faint">
                      {formatRelative(r.updatedAt)}
                    </span>
                    <div className="flex items-center gap-1">
                      <Link
                        to="/recipes/$id/edit"
                        params={{ id: r.id }}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-ink-soft hover:bg-honey-pale hover:text-ink"
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </Link>
                      <RowMenu
                        buttonSize={40}
                        iconSize={16}
                        onViewLog={() => setLogRecipeId(r.id)}
                        onDelete={() => setToDelete(r)}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      {logRecipeId && (
        <RecipeLogDialog
          recipeId={logRecipeId}
          onClose={() => setLogRecipeId(null)}
        />
      )}

      <Dialog open={!!toDelete} onClose={() => setToDelete(null)} title="Delete recipe?">
        <p className="text-ink-soft text-sm">
          This will permanently delete{' '}
          <strong className="text-ink">{toDelete?.title}</strong>. This cannot be undone.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setToDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirmDelete} disabled={del.isPending}>
            {del.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function RowMenu({
  buttonSize,
  iconSize,
  onViewLog,
  onDelete,
}: {
  buttonSize: number;
  iconSize: number;
  onViewLog: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Position the menu against the button via fixed-position viewport coords so
  // it's not clipped by the Card's overflow-hidden. Flip upward near the
  // viewport bottom.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 192; // matches min-w-[12rem]
    const menuHeight = 88; // approx for two items
    const margin = 4;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow >= menuHeight + margin
        ? rect.bottom + margin
        : Math.max(margin, rect.top - menuHeight - margin);
    const left = Math.min(
      Math.max(margin, rect.right - menuWidth),
      window.innerWidth - menuWidth - margin,
    );
    setPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center rounded-lg text-ink-soft hover:bg-cream-deep hover:text-ink"
        style={{ width: buttonSize, height: buttonSize }}
        title="More"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreVertical size={iconSize} />
      </button>
      {open && pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-50 min-w-[12rem] rounded-lg bg-paper shadow-lg overflow-hidden"
            style={{
              top: pos.top,
              left: pos.left,
              border: '1px solid var(--color-hairline)',
            }}
          >
            <button
              type="button"
              role="menuitem"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-ink hover:bg-cream-deep text-left"
              onClick={() => {
                setOpen(false);
                onViewLog();
              }}
            >
              <ScrollText size={14} /> View generation log
            </button>
            <button
              type="button"
              role="menuitem"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--color-error-bg)] text-danger text-left"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
            >
              <Trash2 size={14} /> Delete
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

function SourceBadge({ source }: { source: Recipe['source'] }) {
  switch (source) {
    case 'editorial':
      return <Badge tone="honey">Editorial</Badge>;
    case 'community':
      return <Badge tone="sage">Community</Badge>;
    default:
      return <Badge tone="neutral">User</Badge>;
  }
}

function VisibilityBadge({ visibility }: { visibility: Recipe['visibility'] }) {
  return visibility === 'public' ? (
    <Badge tone="sage">Public</Badge>
  ) : (
    <Badge tone="neutral">Private</Badge>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const delta = Date.now() - d.getTime();
  const s = Math.floor(delta / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toISOString().slice(0, 10);
}
